import { Component, OnInit, Input } from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
} from "@angular/forms";
import {
  ActiveModel,
  AdjuvantModel,
  AdjuvantUnit,
} from "../../../../shared/services/spray-booth-study.service";
import { ExperimentDesignFormValidatorsService } from "../../experiment-design-form/experiment-design-form-validators.service";

@Component({
  selector: "app-spray-booth-study-form",
  templateUrl: "./spray-booth-study-form.component.html",
  styleUrls: ["./spray-booth-study-form.component.css"],
})
export class SprayBoothStudyFormComponent implements OnInit {
  @Input() experimentDesignFormGroup: FormGroup;
  @Input() treatment: FormGroup;
  @Input() isSprayBoothStudy: boolean;
  @Input() sprayStudyOptions: SprayStudyOptions;
  @Input() activeOptions: ActiveModel[];
  @Input() adjuvantOptions: AdjuvantUnit[];
  selectedTab = 0;
  activeIngredient = "";
  concentrationValue = "";
  concentrationUnit = "";

  constructor(
    private fb: FormBuilder,
    private validators: ExperimentDesignFormValidatorsService
  ) {}

  ngOnInit(): void {}

  hasSprayStudyError(sprayStudy: AbstractControl): boolean {
    return sprayStudy.invalid && (sprayStudy.dirty || sprayStudy.touched);
  }

  public get sprayBoothStudy(): FormArray {
    return <FormArray>this.treatment.get("sprayBoothStudy");
  }
  public getActive(index: number): FormArray {
    return <FormArray>this.sprayBoothStudy.at(index).get("actives");
  }

  public getAdjuvant(index: number): FormArray {
    return <FormArray>this.sprayBoothStudy.at(index).get("adjuvants");
  }

  public applicationTimepointId(index: number): FormControl {
    return <FormControl>(
      this.sprayBoothStudy.at(index).get("applicationTimepointId")
    );
  }

  public applicationVolumeId(index: number): FormControl {
    return <FormControl>(
      this.sprayBoothStudy.at(index).get("applicationVolumeId")
    );
  }

  // Active Form Controls
  public activeId(index: number, activeIndex: number): FormControl {
    const activeArray = this.getActive(index);
    if (activeArray.length > 0) {
      return <FormControl>activeArray.at(activeIndex).get("activeId");
    }
    return this.fb.control(null);
  }

  public getActiveIngredient(index: number, activeIndex: number): FormControl {
    return <FormControl>(
      this.getActive(index).at(activeIndex).get("activeIngredient")
    );
  }

  public getConcentrationValue(
    index: number,
    activeIndex: number
  ): FormControl {
    return <FormControl>(
      this.getActive(index).at(activeIndex).get("concentrationValue")
    );
  }

  public getConcentrationUnit(index: number, activeIndex: number): FormControl {
    return <FormControl>(
      this.getActive(index).at(activeIndex).get("concentrationUnit")
    );
  }

  public getRateValue(index: number, activeIndex: number): FormControl {
    return <FormControl>this.getActive(index).at(activeIndex).get("rateValue");
  }

  public getRateUnitId(index: number, activeIndex: number): FormControl {
    return <FormControl>this.getActive(index).at(activeIndex).get("rateUnitId");
  }

  public getRelativeFieldRate(index: number, activeIndex: number): FormControl {
    return <FormControl>(
      this.getActive(index).at(activeIndex).get("relativeFieldRate")
    );
  }

  // Adjuvant Form Controls
  public getAdjuvantId(index: number, adjuvantIndex: number): FormControl {
    return <FormControl>(
      this.getAdjuvant(index).at(adjuvantIndex).get("adjuvantId")
    );
  }

  public getAdjuvantValue(index: number, adjuvantIndex: number): FormControl {
    return <FormControl>(
      this.getAdjuvant(index).at(adjuvantIndex).get("adjuvantValue")
    );
  }

  addActive(index: number) {
    const activeGroup = this.fb.group({
      activeId: [null, [Validators.required]],
      rateValue: [
        "",
        [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)],
      ],
      rateUnitId: [null, [Validators.required]],
      relativeFieldRate: [
        "",
        [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)],
      ],
    });
    (this.getActive(index) as FormArray).push(activeGroup);
    const group = this.sprayBoothStudy.at(index);
    group.markAsDirty();
    group.updateValueAndValidity();
  }

  removeActive(index: number, activeIndex: number) {
    (this.getActive(index) as FormArray).removeAt(activeIndex);
  }

  addAdjuvant(index: number) {
    const adjuvantGroup = this.fb.group({
      adjuvantId: [null, [Validators.required]],
      adjuvantValue: [
        "",
        [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)],
      ],
    });
    (this.getAdjuvant(index) as FormArray).push(adjuvantGroup);
    const group = this.sprayBoothStudy.at(index);
    group.markAsDirty();
    group.updateValueAndValidity();
  }

  removeAdjuvant(index: number, adjuvantIndex: number) {
    (this.getAdjuvant(index) as FormArray).removeAt(adjuvantIndex);
  }

  addSprayEvent(): void {
    const sprayEventGroup = this.fb.group({
      applicationVolumeId: [null, Validators.required],
      applicationTimepointId: [null, Validators.required],
      actives: this.fb.array([]),
      adjuvants: this.fb.array([]),
    });

    sprayEventGroup.setValidators(
      this.validators.atLeastOneActiveOrAdjuvantValidator()
    );
    this.sprayBoothStudy.push(sprayEventGroup);
    sprayEventGroup.updateValueAndValidity();
  }

  getDisplayIngredient(treatmentIndex: number, activeIndex: number): string {
    const activeIdControl = this.getActive(treatmentIndex)
      .at(activeIndex)
      .get("activeId");
    if (!activeIdControl) {
      return "";
    }
    const activeId = +activeIdControl.value;
    const selected = this.activeOptions.find(function (x) {
      return x.id === activeId;
    });
    return selected ? selected.activeIngredient : "";
  }

  getDisplayConcentrationValue(
    treatmentIndex: number,
    activeIndex: number
  ): string {
    const activeId = +this.getActive(treatmentIndex)
      .at(activeIndex)
      .get("activeId").value;
    const selected = this.activeOptions.find(function (x) {
      return x.id === activeId;
    });
    return selected ? selected.concentrationValue : "";
  }

  getDisplayConcentrationUnit(
    treatmentIndex: number,
    activeIndex: number
  ): string {
    const activeId = +this.getActive(treatmentIndex)
      .at(activeIndex)
      .get("activeId").value;
    const selected = this.activeOptions.find(function (x) {
      return x.id === activeId;
    });
    return selected ? selected.concentrationUnit : "";
  }

  getDisplayAdjuvantUnit(
    treatmentIndex: number,
    adjuvantIndex: number
  ): string {
    const adjuvantId = +this.getAdjuvant(treatmentIndex)
      .at(adjuvantIndex)
      .get("adjuvantId").value;

    const selectedAdjuvant = this.adjuvantOptions.find(
      (x) => x.id === adjuvantId
    );

    if (!selectedAdjuvant) {
      return "";
    }

    const unit = this.sprayStudyOptions.adjuvantUnits.find(
      (u) => u.id === selectedAdjuvant.adjuvantUnitId
    );

    return unit ? unit.display : "";
  }

  onTradeNameChange(
    selectedId: string,
    treatmentIndex: number,
    sprayIndex: number,
    activeIndex: number
  ) {
    const id = Number(selectedId);
    const selected = this.activeOptions.find(function (opt) {
      return opt.id === id;
    });

    if (!selected) {
      return;
    }

    const formGroup = this.getActive(treatmentIndex).at(
      sprayIndex
    ) as FormGroup;

    formGroup.controls["activeIngredient"].setValue(selected.activeIngredient);
    formGroup.controls["concentrationValue"].setValue(
      selected.concentrationValue
    );
    formGroup.controls["concentrationUnit"].setValue(
      selected.concentrationUnit
    );
  }

  deleteSprayEvent(index: number): void {
    this.sprayBoothStudy.removeAt(index);
  }

  public copyAllSprayEvents(copyArgs: SprayBoothStudyCopyArgs): void {
    const formArray = this.sprayBoothStudy;

    while (formArray.length > 0) {
      formArray.removeAt(0);
    }

    for (let i = 0; i < copyArgs.sprayEvents.length; i++) {
      const event = copyArgs.sprayEvents[i];

      const newGroup = this.fb.group({
        applicationVolumeId: [event.applicationVolumeId, Validators.required],
        applicationTimepointId: [
          event.applicationTimepointId,
          Validators.required,
        ],
        actives: this.fb.array([]),
        adjuvants: this.fb.array([]),
      });

      const activesArray = newGroup.get("actives") as FormArray;
      for (let j = 0; j < event.actives.length; j++) {
        const active = event.actives[j];
        activesArray.push(
          this.fb.group({
            activeId: [active.activeId, Validators.required],
            rateValue: [
              active.rateValue,
              [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)],
            ],
            rateUnitId: [active.rateUnitId, Validators.required],
            relativeFieldRate: [
              active.relativeFieldRate,
              [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)]
            ],
          })
        );
      }

      const adjuvantsArray = newGroup.get("adjuvants") as FormArray;
      for (let k = 0; k < event.adjuvants.length; k++) {
        const adjuvant = event.adjuvants[k];
        adjuvantsArray.push(
          this.fb.group({
            adjuvantId: [adjuvant.adjuvantId, Validators.required],
            adjuvantValue: [
              adjuvant.adjuvantValue,
              [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)],
            ]
          })
        );
      }

      formArray.push(newGroup);
    }
  }

  clearFormArray(formArray: FormArray): void {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }
}

export interface SprayBoothStudyCopyArgs {
  sprayEvents: {
    applicationVolumeId: number;
    applicationTimepointId: number;
    actives: any[];
    adjuvants: any[];
  }[];
}

export interface SprayStudyOptions {
  herbicideTradeNames: HerbicideTradeNameDisplay[];
  applicationVolumes: ApplicationVolumeDisplay[];
  applicationTimepoints: ApplicationTimepointDisplay[];
  adjuvantNames: AdjuvantDisplay[];
  adjuvantUnits: AdjuvantUnitDisplay[];
  rateUnits: RateUnitDisplay[];
}

export interface HerbicideTradeNameDisplay {
  id: number;
  display: string;
}

export interface ApplicationVolumeDisplay {
  id: number;
  display: string;
}

export interface ApplicationTimepointDisplay {
  id: number;
  display: string;
}

export interface AdjuvantDisplay {
  id: number;
  display: string;
}

export interface AdjuvantUnitDisplay {
  id: number;
  display: string;
}

export interface RateUnitDisplay {
  id: number;
  display: string;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/spray-booth-study/spray-booth-study-form/spray-booth-study-form.component.ts